<ara-generic-dialog
	*transloco="let t"
	[titleKey]="dialogTitleKey()"
	[subtitle]="realEstateTenantUnitLabel()"
	[actions]="actions()"
	[minWidth]="'min(76rem, 96vw)'"
	[maxHeight]="'95vh'"
	(closed)="close()">
	@if (protectedRoleLocked()) {
		<ara-panel class="mt-1" type="warning" header="" [message]="t('tenantunitDetails.editor.protectedRoleReadonly')"></ara-panel>
	} @else {
		<div class="flex flex-col">
			@if (entryContext === 'edit' && showPerson()) {
				<ara-panel class="mt-1" type="info" [message]="t('tenantunitDetails.editor.introEdit')"></ara-panel>
			}

			<!-- Known person (expert) -->
			@if (showKnownPersonSelect()) {
				<div class="py-3.5 border-b border-black/[0.06]">
					<div class="mb-2.5 text-[0.72rem] font-bold uppercase tracking-wide text-black/55">
						{{ t('tenantunitDetails.selectKnownPersonPrompt') }}
					</div>
					<mat-form-field class="w-full max-w-[40rem]">
						<mat-label>{{ t('tenantunitDetails.person') }}</mat-label>
						<mat-select [formControl]="knownPersonControl" (selectionChange)="selectKnownPerson($event)">
							<mat-option [value]="null">{{ t('tenantunitDetails.editor.newPersonInstead') }}</mat-option>
							@for (option of data.personOptions; track option.id) {
								<mat-option [value]="option">{{ option.label }} – {{ option.rolesLabel }} – {{ option.datesLabel }}</mat-option>
							}
						</mat-select>
					</mat-form-field>
					@if (isExistingPerson()) {
						<div class="mode-banner">
							<mat-icon class="shrink-0">edit</mat-icon>
							<span>{{ t('tenantunitDetails.editor.editingPersonHint') }}</span>
						</div>
					}
				</div>
			}

			<!-- Person (Anlegen & Bearbeiten – Anrede/Titel/Name inline editierbar) -->
			@if (showPerson()) {
				<div class="py-3.5 border-b border-black/[0.06]">
					<div class="mb-2.5 text-[0.72rem] font-bold uppercase tracking-wide text-black/55">{{ t('tenantunitDetails.person') }}</div>
					<div class="flex flex-row flex-wrap gap-3">
						<mat-form-field class="flex-1 basis-[10rem] min-w-[9rem]">
							<mat-label>{{ t('user.salutation') }}</mat-label>
							<mat-select [formControl]="personForm.controls.salutation">
								@for (option of salutations; track option.value) {
									<mat-option [value]="option.value">{{ option.label }}</mat-option>
								}
							</mat-select>
							<mat-error>{{ t('tenantunitDetails.salutationRequired') }}</mat-error>
						</mat-form-field>

						@if (!isFullNameOnly()) {
							<mat-form-field class="flex-1 basis-[9rem] min-w-[8rem]">
								<mat-label>{{ t('user.title') }}</mat-label>
								<input [formControl]="personForm.controls.title" [matAutocomplete]="titleAuto" matInput />
								<mat-autocomplete #titleAuto="matAutocomplete">
									@for (option of titleOptions; track option) {
										<mat-option [value]="option">{{ option }}</mat-option>
									}
								</mat-autocomplete>
							</mat-form-field>
							<mat-form-field class="flex-1 basis-[13rem] min-w-[10rem]">
								<mat-label>{{ t('user.firstname') }}</mat-label>
								<input [formControl]="personForm.controls.givenName" matInput />
							</mat-form-field>
							<mat-form-field class="flex-1 basis-[13rem] min-w-[10rem]">
								<mat-label>{{ t('user.lastname') }}</mat-label>
								<input [formControl]="personForm.controls.familyName" matInput required />
								<mat-error>{{ t('errors.checkInput') }}</mat-error>
							</mat-form-field>
						} @else {
							<mat-form-field class="flex-1 basis-[24rem] min-w-[14rem]">
								<mat-label>{{ t('user.fullName') }}</mat-label>
								<input [formControl]="personForm.controls.fullName" matInput required />
								<mat-error>{{ t('errors.checkInput') }}</mat-error>
							</mat-form-field>
						}
					</div>

					<div class="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
						<mat-form-field class="flex-1 basis-[22rem] min-w-[14rem] max-w-[28rem]" subscriptSizing="dynamic">
							<mat-label>{{ t('user.email') }}</mat-label>
							<input [formControl]="personForm.controls.email" matInput type="email" [required]="portalAccessSelected()" />
							<mat-error>
								@if (personForm.controls.email.hasError('required')) {
									{{ t('errors.checkInput') }}
								} @else {
									{{ t('common.enterValidMail') }}
								}
							</mat-error>
						</mat-form-field>
					</div>
				</div>
			}

			<!-- Roles -->
			<div class="py-3.5 border-b border-black/[0.06]">
				<div class="mb-2.5 text-[0.72rem] font-bold uppercase tracking-wide text-black/55">{{ t('tenantunitDetails.roles') }}</div>
				<!-- Zell-Templates der Rollen-Tabelle (ara-inline-edit-table); Kontext: row = { group } -->
				<ng-template #fromCell let-row>
					<div class="flex flex-row items-center gap-1">
						<mat-form-field class="role-date" subscriptSizing="dynamic">
							<mat-label>{{ t('tenantunitDetails.roleBeginDate') }}</mat-label>
							<input
								matInput
								[matDatepicker]="fromPicker"
								[matDatepickerFilter]="dateFilterFor(row.group)"
								[min]="minValidFrom(row.group)"
								[max]="maxValidFrom(row.group)"
								[formControl]="roleControl(row.group, 'validFrom')"
								(dateChange)="setRoleValidFrom(row.group, $event.value)" />
							<mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
							<mat-datepicker #fromPicker></mat-datepicker>
						</mat-form-field>
						@if (isNonLatestRoleLocked(row.group)) {
							<mat-icon
								class="shrink-0 text-black/35 !text-[18px] !w-[18px] !h-[18px]"
								matTooltip="{{ t('tenantunitDetails.editor.roleNotLatestLocked') }}">
								lock
							</mat-icon>
						} @else if (isVacancyStartLocked(row.group)) {
							<mat-icon
								class="shrink-0 text-black/35 !text-[18px] !w-[18px] !h-[18px]"
								matTooltip="{{ t('tenantunitDetails.editor.vacancyStartLocked') }}">
								lock
							</mat-icon>
						}
						@if (roleGroups().length > 1 && roleControl(row.group, 'validFrom').value) {
							<button
								araButton="icon"
								color="secondary"
								type="button"
								class="shrink-0 text-black/45"
								(click)="copyMoveInToAllRoles(row.group)"
								matTooltip="{{ t('tenantunitDetails.editor.copyMoveInToAll') }}">
								<mat-icon>content_copy</mat-icon>
							</button>
						}
					</div>
				</ng-template>

				<ng-template #toCell let-row>
					@if (roleTypeOf(row.group) === UiRoleType.RESIDENT) {
						@if (roleControl(row.group, 'validTo').value) {
							<!-- Bereits ausgezogen: Auszugsdatum sichtbar (Änderung des Auszugs weiterhin über Leerstand). -->
							<span class="ended-date-chip">
								<mat-icon>logout</mat-icon>
								{{ roleDateLabel(roleControl(row.group, 'validTo').value) }}
							</span>
						} @else {
							<span class="lock-chip">
								<mat-icon>lock</mat-icon>
								{{ t('tenantunitDetails.editor.moveOutViaVacancy') }}
							</span>
						}
					} @else if (roleTypeOf(row.group) === UiRoleType.VACANCY) {
						<span class="text-black/25">—</span>
					} @else if (isNonLatestRoleLocked(row.group)) {
						<span class="lock-chip" matTooltip="{{ t('tenantunitDetails.editor.roleNotLatestLocked') }}">
							<mat-icon>lock</mat-icon>
							{{ roleDateLabel(roleControl(row.group, 'validTo').value) }}
						</span>
					} @else {
						<mat-form-field class="role-date" subscriptSizing="dynamic">
							<mat-label>{{ t('tenantunitDetails.roleEndDate') }}</mat-label>
							<input matInput [matDatepicker]="toPicker" [formControl]="roleControl(row.group, 'validTo')" />
							<mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
							<mat-datepicker #toPicker></mat-datepicker>
						</mat-form-field>
					}
				</ng-template>

				<ng-template #tenantNumberCell let-row>
					@if (showTenantNumberCell(row.group)) {
						<mat-form-field class="role-input" subscriptSizing="dynamic">
							<mat-label>{{ t('tenantunitDetails.tenantNumber') }}</mat-label>
							<input matInput [formControl]="roleControl(row.group, 'tenantNumber')" />
						</mat-form-field>
					} @else {
						<span class="text-black/25">—</span>
					}
				</ng-template>

				<!-- Zustellart: beim Anlegen mit „Manuelle Zustellung" vorbelegt und nur informativ — änderbar
				 erst an der bestehenden Bewohner-Rolle, und auch dort nur mit dem Recht aus der Liegenschaft. -->
				<ng-template #uviCell let-row>
					@if (roleTypeOf(row.group) === UiRoleType.RESIDENT) {
						@if (canEditUvi(row.group)) {
							<mat-form-field class="role-select" subscriptSizing="dynamic">
								<mat-label>{{ t('tenantunitDetails.uviOptions') }}</mat-label>
								<mat-select [formControl]="roleControl(row.group, 'uviReception')">
									@for (option of uviOptionsFor(row.group); track option.value) {
										<mat-option [value]="option.value">{{ option.label }}</mat-option>
									}
								</mat-select>
							</mat-form-field>
						} @else {
							<span class="text-sm text-black/55" matTooltip="{{ t(uviReadonlyHintKey(row.group)) }}">{{ uviReadonlyLabel(row.group) }}</span>
						}
					} @else {
						<span class="text-black/25">—</span>
					}
				</ng-template>

				<!-- Portal-Zugang der Rolle: Häkchen nur, wenn diese Rolle einen Zugang bekommen kann und
					 noch keinen hat; registrierte Rollen zeigen ihren Zustand, nicht versendbare einen Strich. -->
				<ng-template #portalCell let-row>
					@if (portalAccessCell(row.group); as cell) {
						@if (cell.row; as portalRow) {
							<div class="flex flex-row items-center gap-1">
								<ara-checkbox
									[checked]="portalRow.checked"
									(change)="onPortalAccessToggle(portalRow, $event)"
									[matTooltip]="portalRow.tooltip"
									[aria-label]="portalRow.tooltip"
									name="send-portal-access-cy"></ara-checkbox>
								@if (portalRow.action === 'resend') {
									<span class="text-xs text-black/55">{{ t('tenantunitDetails.editor.portalAccessResendHint') }}</span>
								}
							</div>
						} @else if (cell.kind === 'registered') {
							<span class="text-sm text-black/55" [matTooltip]="cell.tooltip ?? ''">
								{{ t('tenantunitDetails.editor.portalAccessRegistered') }}
							</span>
						} @else if (cell.kind === 'loading') {
							<span class="text-black/25">…</span>
						} @else if (cell.kind === 'failed') {
							<span class="text-sm text-warn" [matTooltip]="cell.tooltip ?? ''">{{ t('tenantunitDetails.editor.portalAccessFailedShort') }}</span>
						} @else {
							<span class="text-black/25">—</span>
						}
					}
				</ng-template>

				<ng-template #roleActionCell let-row let-i="index">
					@if (roleGroups().length > 1 && isNewRole(row.group)) {
						<button araButton="icon" type="button" color="error" (click)="removeRole(i)" matTooltip="{{ t('commonActions.delete') }}">
							<mat-icon>delete</mat-icon>
						</button>
					}
				</ng-template>

				<!-- Add-Zeile der Rollen-Tabelle: eine Zeile pro anhängbarem Rollentyp, zentriert wie in
				 allen anderen Tabellen (kanonische Add-Zeile). -->
				<ng-template #roleAddRow>
					@if (availableAddRoleTypes().length > 0) {
						@for (roleType of availableAddRoleTypes(); track roleType) {
							<button araButton color="primary" type="button" (click)="addRoleType(roleType)">
								<mat-icon>add</mat-icon>
								<span>{{ getRoleTypeLabel(roleType) }}</span>
							</button>
						}
					} @else {
						<span class="text-xs italic text-black/50">{{ addRoleDisabledReason() }}</span>
					}
				</ng-template>

				<!-- Ein Strang je Rollentyp: Vorgänger als graue Kontextzeile mit editierbarem Ende, darunter
				 die Zeilen dieser Person, darunter der Nachfolger. Bewohner und Leerstand teilen sich
				 einen Strang. -->
				@if (roleColumns().length) {
					@for (strand of visibleStrands(); track strand.roleType) {
						<div class="ara-table-group-header mt-3">{{ getRoleTypeLabel(strand.roleType) }}</div>

						<ng-template #previousRow>
							@if (strand.previous; as previous) {
								<span class="shrink-0 text-black/55">{{ t('tenantunitDetails.editor.strandPrevious') }}</span>
								<span class="font-semibold">{{ previous.name }}</span>
								@if (isBoundaryEditable(strand.roleType)) {
									<mat-form-field class="role-date" subscriptSizing="dynamic">
										<mat-label>{{ t('tenantunitDetails.roleEndDate') }}</mat-label>
										<input matInput [matDatepicker]="boundaryPicker" [formControl]="boundaryControl(previous.roleId)" />
										<mat-datepicker-toggle matSuffix [for]="boundaryPicker"></mat-datepicker-toggle>
										<mat-datepicker #boundaryPicker></mat-datepicker>
									</mat-form-field>
								} @else {
									<span class="lock-chip" matTooltip="{{ t('tenantunitDetails.editor.boundaryFollowsMoveIn') }}">
										<mat-icon>lock</mat-icon>
										{{ t('tenantunitDetails.roleEndDate') }}: {{ roleDateLabel(boundaryControl(previous.roleId).value) }}
									</span>
								}
							}
						</ng-template>

						<ng-template #nextRow>
							@if (strand.next; as next) {
								<span class="shrink-0 text-black/55">{{ t('tenantunitDetails.editor.strandNext') }}</span>
								<span class="font-semibold">{{ next.name }}</span>
								<span class="text-black/55">{{ t('tenantunitDetails.roleBeginDate') }}: {{ roleDateLabel(next.validFrom) }}</span>
							}
						</ng-template>

						<ara-inline-edit-table
							[rows]="strandRows(strand.roleType)"
							[columns]="roleColumns()"
							[actionTemplate]="roleActionCell"
							[beforeTableTemplate]="strand.previous ? previousRow : null"
							[afterTableTemplate]="strand.next ? nextRow : null"></ara-inline-edit-table>
					}

					<div class="ara-table-add-row--inline mt-3">
						<ng-container *ngTemplateOutlet="roleAddRow"></ng-container>
					</div>
				}

				@if (validation().hard.length > 0) {
					<ara-panel class="mt-2" type="error" header="" @fadeInUp>
						@for (message of validation().hard; track message) {
							<div>{{ message }}</div>
						}
					</ara-panel>
				}
			</div>

			@if (addressToggleVisible()) {
				<div class="py-3.5 border-b border-black/[0.06]">
					<div class="mb-1 text-[0.72rem] font-bold uppercase tracking-wide text-black/55">{{ t('address.deliveryAddress') }}</div>
					<div class="mb-2.5 text-xs text-black/55">
						{{ addressRequired() ? t('address.requiredBecauseUvi') : t('address.appliesToAllRoles') }}
					</div>
					<ara-slide-toggle [checked]="addressVisible()" [disabled]="addressRequired()" (change)="onShowAddressToggle($event)">
						{{ t('address.deliveryAddress') }}
					</ara-slide-toggle>
					@if (!addressVisible()) {
						<ara-panel class="mt-2" type="info" header="" [message]="t('address.uviFallbackHint')"></ara-panel>
					}
					@if (addressVisible()) {
						<div @fadeInUp>
							@if (hasRealEstateAddress()) {
								<button araButton color="secondary" type="button" class="mt-2" (click)="copyRealEstateAddress()">
									<mat-icon>content_copy</mat-icon>
									<span>{{ t('address.copyFromRealEstate') }}</span>
								</button>
							}
							<div class="flex flex-row flex-wrap gap-3 mt-3">
								<mat-form-field class="basis-[70%] grow">
									<mat-label>{{ t('address.street') }}</mat-label>
									<input matInput [formControl]="addressForm.controls.street" [required]="addressVisible()" />
								</mat-form-field>
								<mat-form-field class="basis-[20%] grow">
									<mat-label>{{ t('address.houseNumber') }}</mat-label>
									<input matInput [formControl]="addressForm.controls.houseNumber" [required]="addressVisible()" />
								</mat-form-field>
							</div>
							<div class="flex flex-row flex-wrap gap-3">
								<mat-form-field class="basis-[28%] grow">
									<mat-label>{{ t('country.country') }}</mat-label>
									<mat-select
										[formControl]="addressForm.controls.country"
										[required]="addressVisible()"
										(selectionChange)="onCountryChange($event)">
										<mat-option>
											<ngx-mat-select-search
												[formControl]="countryFilterControl"
												[placeholderLabel]="t('country.country')"></ngx-mat-select-search>
										</mat-option>
										@for (country of filteredCountries(); track country.value) {
											<mat-option [value]="country.value">{{ country.translation }}</mat-option>
										}
									</mat-select>
								</mat-form-field>
								<mat-form-field class="basis-[20%] grow">
									<mat-label>{{ t('address.zipcode') }}</mat-label>
									<input matInput [formControl]="addressForm.controls.zipCode" [required]="addressVisible()" />
								</mat-form-field>
								<mat-form-field class="basis-[40%] grow">
									<mat-label>{{ t('address.city') }}</mat-label>
									<input matInput [formControl]="addressForm.controls.city" [required]="addressVisible()" />
								</mat-form-field>
							</div>
						</div>
					}
				</div>
			}

			<!-- Consequences -->
			<div class="py-3.5">
				<div class="mb-2.5 text-[0.72rem] font-bold uppercase tracking-wide text-black/55">{{ t('tenantunitDetails.editor.consequencesTitle') }}</div>
				<ara-panel type="info" header="">
					@if (consequences().length > 0) {
						<ul class="list-disc pl-5 m-0">
							@for (item of consequences(); track item) {
								<li>{{ item }}</li>
							}
						</ul>
					} @else {
						<span class="italic">{{ t('tenantunitDetails.editor.noConsequences') }}</span>
					}
				</ara-panel>
			</div>
		</div>
	}
</ara-generic-dialog>
